import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../../components/Layout';
import { toast } from 'sonner';
import { Save, FileDown, Mail, Send, Plus, Trash2, ArrowLeft, CreditCard, Landmark, Bell } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const DOC_TITLES = { quote: 'Presupuesto', delivery_note: 'Albarán', invoice: 'Factura', rectifying_invoice: 'Factura rectificativa' };
const BASE_PATHS = { quote: '/billing/quotes', delivery_note: '/billing/delivery-notes', invoice: '/billing/invoices' };

const money = (v) => (v ?? 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

const emptyLine = () => ({ id: crypto.randomUUID(), concepto: '', descripcion_larga: '', cantidad: 1, precio_unitario: 0, descuento_porcentaje: 0, iva_porcentaje: 21, unidad: 'ud', product_id: null });

const SalesDocEditor = ({ docType }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [doc, setDoc] = useState(null);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);

  const [form, setForm] = useState({
    client_id: '',
    fecha_emision: new Date().toISOString().slice(0, 10),
    fecha_vencimiento: '',
    fecha_validez: '',
    referencia: '',
    lineas: [emptyLine()],
    notas: '',
    condiciones: '',
    retencion_porcentaje: 0,
    forma_pago: 'Transferencia',
    exento_iva: false,
    motivo_exencion: '',
  });

  const fetchMasters = useCallback(async () => {
    try {
      const [c, p, cp] = await Promise.all([
        axios.get(`${API_URL}/billing/clients?activo=true`),
        axios.get(`${API_URL}/billing/products?activo=true`),
        axios.get(`${API_URL}/billing/products/from-courses`),
      ]);
      setClients(c.data);
      setProducts([...p.data, ...cp.data]);
    } catch (e) {
      toast.error('Error al cargar datos maestros');
    }
  }, []);

  useEffect(() => { fetchMasters(); }, [fetchMasters]);

  useEffect(() => {
    if (isNew) return;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/billing/sales-documents/${id}`);
        setDoc(res.data);
        setForm({
          client_id: res.data.client_id || '',
          fecha_emision: res.data.fecha_emision,
          fecha_vencimiento: res.data.fecha_vencimiento || '',
          fecha_validez: res.data.fecha_validez || '',
          referencia: res.data.referencia || '',
          lineas: (res.data.lineas || []).map(l => ({ ...l })),
          notas: res.data.notas || '',
          condiciones: res.data.condiciones || '',
          retencion_porcentaje: res.data.retencion_porcentaje || 0,
          forma_pago: res.data.forma_pago || 'Transferencia',
          exento_iva: res.data.exento_iva || false,
          motivo_exencion: res.data.motivo_exencion || '',
        });
      } catch (e) {
        toast.error('No se encontró el documento');
        navigate(BASE_PATHS[docType]);
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isNew, docType, navigate]);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  // Cálculos locales
  const computeLineTotal = (l) => {
    const sub = (Number(l.cantidad) || 0) * (Number(l.precio_unitario) || 0) * (1 - (Number(l.descuento_porcentaje) || 0) / 100);
    const iva = sub * (Number(l.iva_porcentaje) || 0) / 100;
    return { subtotal: sub, importe_iva: iva, total: sub + iva };
  };

  const totals = React.useMemo(() => {
    let base = 0, iva = 0, subtotal_bruto = 0, descuentos = 0;
    for (const l of form.lineas) {
      const c = Number(l.cantidad) || 0, p = Number(l.precio_unitario) || 0, dp = Number(l.descuento_porcentaje) || 0;
      subtotal_bruto += c * p;
      descuentos += c * p * dp / 100;
      const t = computeLineTotal(l);
      base += t.subtotal;
      if (!form.exento_iva) iva += t.importe_iva;
    }
    const ret = base * (Number(form.retencion_porcentaje) || 0) / 100;
    return {
      subtotal: subtotal_bruto, total_descuento: descuentos, base_imponible: base,
      total_iva: form.exento_iva ? 0 : iva, total_retencion: ret,
      total: base + (form.exento_iva ? 0 : iva) - ret,
    };
  }, [form.lineas, form.exento_iva, form.retencion_porcentaje]);

  const addLine = () => set('lineas', [...form.lineas, emptyLine()]);
  const removeLine = (idx) => set('lineas', form.lineas.filter((_, i) => i !== idx));
  const updateLine = (idx, patch) => {
    const next = [...form.lineas];
    next[idx] = { ...next[idx], ...patch };
    set('lineas', next);
  };

  const applyProduct = (idx, productId) => {
    const p = products.find(x => x.id === productId);
    if (!p) return;
    updateLine(idx, {
      concepto: p.nombre,
      descripcion_larga: p.descripcion || '',
      precio_unitario: p.precio_unitario,
      iva_porcentaje: p.iva_porcentaje,
      unidad: p.unidad || 'ud',
      product_id: productId,
    });
  };

  const buildPayload = () => ({
    document_type: docType,
    client_id: form.client_id || null,
    fecha_emision: form.fecha_emision,
    fecha_vencimiento: form.fecha_vencimiento || null,
    fecha_validez: form.fecha_validez || null,
    referencia: form.referencia || null,
    lineas: form.lineas.filter(l => l.concepto).map(l => ({
      id: l.id, concepto: l.concepto, descripcion_larga: l.descripcion_larga || null,
      cantidad: Number(l.cantidad) || 0, precio_unitario: Number(l.precio_unitario) || 0,
      descuento_porcentaje: Number(l.descuento_porcentaje) || 0,
      iva_porcentaje: Number(l.iva_porcentaje) || 0,
      unidad: l.unidad || 'ud', product_id: l.product_id || null,
    })),
    notas: form.notas || null, condiciones: form.condiciones || null,
    retencion_porcentaje: Number(form.retencion_porcentaje) || 0,
    forma_pago: form.forma_pago, exento_iva: !!form.exento_iva,
    motivo_exencion: form.exento_iva ? form.motivo_exencion : null,
  });

  const handleSave = async () => {
    if (!form.client_id) return toast.error('Selecciona un cliente');
    if (form.lineas.filter(l => l.concepto).length === 0) return toast.error('Añade al menos una línea con concepto');
    setSaving(true);
    try {
      if (isNew) {
        const res = await axios.post(`${API_URL}/billing/sales-documents`, buildPayload());
        toast.success('Borrador guardado');
        navigate(BASE_PATHS[docType] + '/' + res.data.id, { replace: true });
      } else {
        const payload = buildPayload();
        delete payload.document_type;
        const res = await axios.put(`${API_URL}/billing/sales-documents/${id}`, payload);
        setDoc(res.data);
        toast.success('Cambios guardados');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleIssue = async () => {
    if (!window.confirm(`¿Emitir ${DOC_TITLES[docType].toLowerCase()}? Se asignará un número definitivo y ya no podrá editarse.`)) return;
    try {
      await handleSave();
      const res = await axios.post(`${API_URL}/billing/sales-documents/${id || doc?.id}/issue`);
      setDoc(res.data);
      toast.success(`Emitido: ${res.data.numero}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al emitir');
    }
  };

  const openPdf = () => {
    if (!doc?.id) return;
    const token = localStorage.getItem('token');
    axios.get(`${API_URL}/billing/sales-documents/${doc.id}/pdf`, { responseType: 'blob', headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        const blob = new Blob([r.data], { type: 'application/pdf' });
        window.open(URL.createObjectURL(blob), '_blank');
      })
      .catch(() => toast.error('Error PDF'));
  };

  const downloadFaceXml = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await axios.post(`${API_URL}/billing/sales-documents/${doc.id}/face/generate`, {}, {
        responseType: 'blob', headers: { Authorization: `Bearer ${token}` },
      });
      const blob = new Blob([r.data], { type: 'application/xml' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `factura_${doc.numero.replace('/', '-')}.xsig`;
      a.click();
      toast.success('XML Facturae firmado y descargado');
      const upd = await axios.get(`${API_URL}/billing/sales-documents/${doc.id}`);
      setDoc(upd.data);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Error generando XML';
      toast.error(msg);
    }
  };

  const previewFaceXml = () => {
    const token = localStorage.getItem('token');
    axios.get(`${API_URL}/billing/sales-documents/${doc.id}/face/preview`, {
      responseType: 'blob', headers: { Authorization: `Bearer ${token}` },
    }).then(r => {
      const blob = new Blob([r.data], { type: 'application/xml' });
      window.open(URL.createObjectURL(blob), '_blank');
    }).catch(err => toast.error(err.response?.data?.detail || 'Error preview'));
  };

  const sendReminder = async () => {
    try {
      await axios.post(`${API_URL}/billing/sales-documents/${doc.id}/send-reminder`);
      toast.success('Recordatorio de cobro enviado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const [emailForm, setEmailForm] = useState({ to: '', subject: '', body: '' });
  const openEmailDialog = () => {
    setEmailForm({ to: doc?.client_data?.email_facturacion || doc?.client_data?.email || '', subject: '', body: '' });
    setShowEmailDialog(true);
  };
  const sendEmail = async () => {
    if (!emailForm.to) return toast.error('Falta destinatario');
    try {
      await axios.post(`${API_URL}/billing/sales-documents/${doc.id}/email`, emailForm);
      toast.success('Email en cola de envío');
      setShowEmailDialog(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const changeStatus = async (nuevo) => {
    try {
      const res = await axios.patch(`${API_URL}/billing/sales-documents/${doc.id}/status`, { estado: nuevo });
      setDoc(res.data);
      toast.success('Estado actualizado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const [payForm, setPayForm] = useState({ fecha: new Date().toISOString().slice(0,10), importe: '', metodo: 'Transferencia', notas: '' });
  const addPayment = async () => {
    if (!payForm.importe) return toast.error('Introduce el importe');
    try {
      const res = await axios.post(`${API_URL}/billing/sales-documents/${doc.id}/payments`, {
        ...payForm, importe: parseFloat(payForm.importe),
      });
      setDoc(res.data);
      toast.success('Pago registrado');
      setShowPaymentDialog(false);
      setPayForm({ fecha: new Date().toISOString().slice(0,10), importe: '', metodo: 'Transferencia', notas: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  if (loading) return <Layout><div className="p-12 text-center text-slate-500">Cargando...</div></Layout>;

  const editable = !doc || doc.estado === 'borrador';
  const canIssue = doc && doc.estado === 'borrador';
  const isInvoice = docType === 'invoice';

  return (
    <Layout>
      <div className="space-y-6" data-testid={`salesdoc-editor-${docType}`}>
        {/* Cabecera */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(BASE_PATHS[docType])} className="text-slate-500 hover:text-slate-900">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-manrope font-bold text-slate-900">
                {DOC_TITLES[docType]} {doc?.numero && doc.numero !== '(borrador)' ? `#${doc.numero}` : ''}
              </h1>
              {doc?.estado && (
                <span className="inline-block mt-1 text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-700 capitalize">
                  {doc.estado}
                </span>
              )}
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {editable && (
              <Button data-testid="salesdoc-save-button" onClick={handleSave} disabled={saving} variant="outline">
                <Save className="w-4 h-4 mr-1" /> {saving ? 'Guardando...' : 'Guardar borrador'}
              </Button>
            )}
            {canIssue && (
              <Button data-testid="salesdoc-issue-button" onClick={handleIssue} className="bg-[#004E89] hover:bg-[#003a68]">
                <Send className="w-4 h-4 mr-1" /> Emitir
              </Button>
            )}
            {doc && !editable && (
              <>
                <Button onClick={openPdf} variant="outline"><FileDown className="w-4 h-4 mr-1" /> PDF</Button>
                <Button onClick={openEmailDialog} variant="outline"><Mail className="w-4 h-4 mr-1" /> Enviar</Button>
                {isInvoice && (doc.client_data?.is_public_entity || doc.face_generated) && (
                  <>
                    <Button data-testid="face-preview-btn" onClick={previewFaceXml} variant="outline" title="Ver XML Facturae sin firmar">
                      <Landmark className="w-4 h-4 mr-1" /> XML (preview)
                    </Button>
                    <Button data-testid="face-generate-btn" onClick={downloadFaceXml} className="bg-red-700 hover:bg-red-800 text-white" title="Genera XML Facturae 3.2.2 firmado con XAdES para subir a FACe">
                      <Landmark className="w-4 h-4 mr-1" /> Firmar FACe {doc.face_signed && '✓'}
                    </Button>
                  </>
                )}
                {isInvoice && !['pagada','anulada','borrador'].includes(doc.estado) && (
                  <Button onClick={sendReminder} variant="outline" data-testid="send-reminder-btn">
                    <Bell className="w-4 h-4 mr-1" /> Recordatorio
                  </Button>
                )}
                {isInvoice && doc.estado !== 'pagada' && doc.estado !== 'anulada' && (
                  <Button onClick={() => setShowPaymentDialog(true)} className="bg-green-600 hover:bg-green-700">
                    <CreditCard className="w-4 h-4 mr-1" /> Registrar pago
                  </Button>
                )}
              </>
            )}
          </div>
        </div>

        {/* Estado avanzado (para docs emitidos) */}
        {doc && !editable && (
          <div className="bg-white border border-slate-200 rounded-lg p-3 flex flex-wrap gap-2 items-center">
            <span className="text-sm text-slate-500">Cambiar estado:</span>
            {docType === 'quote' && ['aceptado','rechazado','caducado'].map(s => (
              <Button key={s} size="sm" variant="outline" onClick={()=>changeStatus(s)}>{s}</Button>
            ))}
            {docType === 'delivery_note' && ['facturado','anulado'].map(s => (
              <Button key={s} size="sm" variant="outline" onClick={()=>changeStatus(s)}>{s}</Button>
            ))}
            {isInvoice && ['vencida','anulada'].map(s => (
              <Button key={s} size="sm" variant="outline" onClick={()=>changeStatus(s)}>{s}</Button>
            ))}
          </div>
        )}

        {/* Cliente y fechas */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2">
            <Label>Cliente *</Label>
            <select disabled={!editable} value={form.client_id} onChange={e=>set('client_id', e.target.value)} className="w-full mt-1 rounded-md border border-slate-300 px-3 py-2 text-sm" data-testid="salesdoc-client-select">
              <option value="">— Selecciona un cliente —</option>
              {clients.map(c => (
                <option key={c.id} value={c.id}>{c.nombre_fiscal} ({c.cif_nif})</option>
              ))}
            </select>
          </div>
          <div>
            <Label>Fecha emisión *</Label>
            <Input disabled={!editable} type="date" value={form.fecha_emision} onChange={e=>set('fecha_emision', e.target.value)} />
          </div>
          {docType === 'invoice' && (
            <div>
              <Label>Vencimiento</Label>
              <Input disabled={!editable} type="date" value={form.fecha_vencimiento} onChange={e=>set('fecha_vencimiento', e.target.value)} />
            </div>
          )}
          {docType === 'quote' && (
            <div>
              <Label>Válido hasta</Label>
              <Input disabled={!editable} type="date" value={form.fecha_validez} onChange={e=>set('fecha_validez', e.target.value)} />
            </div>
          )}
          <div className="md:col-span-2">
            <Label>Referencia</Label>
            <Input disabled={!editable} value={form.referencia} onChange={e=>set('referencia', e.target.value)} placeholder="Nº pedido cliente / expediente" />
          </div>
        </div>

        {/* Líneas */}
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-manrope font-semibold text-slate-900">Líneas</h3>
            {editable && <Button onClick={addLine} size="sm" variant="outline"><Plus className="w-4 h-4 mr-1" /> Añadir línea</Button>}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-slate-500 uppercase">
                <tr className="border-b">
                  <th className="text-left p-2 w-72">Concepto</th>
                  <th className="text-left p-2 w-56">Producto/Servicio</th>
                  <th className="text-right p-2 w-20">Cant.</th>
                  <th className="text-right p-2 w-24">Precio</th>
                  <th className="text-right p-2 w-16">Dto%</th>
                  <th className="text-right p-2 w-16">IVA%</th>
                  <th className="text-right p-2 w-28">Subtotal</th>
                  {editable && <th className="w-8"></th>}
                </tr>
              </thead>
              <tbody>
                {form.lineas.map((l, idx) => {
                  const t = computeLineTotal(l);
                  return (
                    <tr key={l.id} className="border-b">
                      <td className="p-1">
                        <Input disabled={!editable} value={l.concepto} onChange={e=>updateLine(idx, { concepto: e.target.value })} placeholder="Concepto..." />
                        <Input disabled={!editable} value={l.descripcion_larga || ''} onChange={e=>updateLine(idx, { descripcion_larga: e.target.value })} placeholder="Descripción larga (opcional)" className="mt-1 text-xs" />
                      </td>
                      <td className="p-1">
                        <select disabled={!editable} value={l.product_id || ''} onChange={e => applyProduct(idx, e.target.value)} className="w-full rounded-md border border-slate-300 px-2 py-1 text-xs">
                          <option value="">— Personalizado —</option>
                          {products.map(p => (<option key={p.id} value={p.id}>{p.nombre.slice(0, 40)}</option>))}
                        </select>
                      </td>
                      <td className="p-1"><Input disabled={!editable} type="number" step="0.01" value={l.cantidad} onChange={e=>updateLine(idx, { cantidad: e.target.value })} className="text-right" /></td>
                      <td className="p-1"><Input disabled={!editable} type="number" step="0.01" value={l.precio_unitario} onChange={e=>updateLine(idx, { precio_unitario: e.target.value })} className="text-right" /></td>
                      <td className="p-1"><Input disabled={!editable} type="number" step="0.01" value={l.descuento_porcentaje} onChange={e=>updateLine(idx, { descuento_porcentaje: e.target.value })} className="text-right" /></td>
                      <td className="p-1">
                        <select disabled={!editable} value={l.iva_porcentaje} onChange={e=>updateLine(idx, { iva_porcentaje: parseFloat(e.target.value) })} className="w-full rounded-md border border-slate-300 px-2 py-1 text-xs text-right">
                          <option value={0}>0%</option><option value={4}>4%</option><option value={10}>10%</option><option value={21}>21%</option>
                        </select>
                      </td>
                      <td className="p-2 text-right font-medium">{money(t.subtotal)}</td>
                      {editable && (
                        <td className="p-1">
                          <button onClick={()=>removeLine(idx)} className="text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Totales + Opciones */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
            <h3 className="font-manrope font-semibold text-slate-900">Opciones</h3>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="exento" checked={form.exento_iva} onChange={e=>set('exento_iva', e.target.checked)} disabled={!editable} />
              <label htmlFor="exento" className="text-sm">Exento de IVA (servicios sanitarios, etc.)</label>
            </div>
            {form.exento_iva && (
              <div>
                <Label>Motivo exención</Label>
                <Input disabled={!editable} value={form.motivo_exencion} onChange={e=>set('motivo_exencion', e.target.value)} placeholder="art. 20 LIVA - servicios sanitarios" />
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Retención IRPF (%)</Label>
                <Input disabled={!editable} type="number" step="0.01" value={form.retencion_porcentaje} onChange={e=>set('retencion_porcentaje', e.target.value)} />
              </div>
              <div>
                <Label>Forma de pago</Label>
                <select disabled={!editable} value={form.forma_pago} onChange={e=>set('forma_pago', e.target.value)} className="w-full mt-1 rounded-md border border-slate-300 px-3 py-2 text-sm">
                  <option>Transferencia</option><option>Domiciliación</option><option>Efectivo</option><option>Tarjeta</option><option>Otro</option>
                </select>
              </div>
            </div>
            <div>
              <Label>Condiciones</Label>
              <textarea disabled={!editable} value={form.condiciones} onChange={e=>set('condiciones', e.target.value)} className="w-full mt-1 rounded-md border border-slate-300 px-3 py-2 text-sm" rows={2} />
            </div>
            <div>
              <Label>Notas / Observaciones</Label>
              <textarea disabled={!editable} value={form.notas} onChange={e=>set('notas', e.target.value)} className="w-full mt-1 rounded-md border border-slate-300 px-3 py-2 text-sm" rows={2} />
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <h3 className="font-manrope font-semibold text-slate-900 mb-3">Totales</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-600">Base imponible:</span><span className="font-medium">{money(totals.base_imponible)}</span></div>
              {totals.total_descuento > 0 && <div className="flex justify-between text-slate-500"><span>Descuentos:</span><span>- {money(totals.total_descuento)}</span></div>}
              <div className="flex justify-between"><span className="text-slate-600">IVA {form.exento_iva ? '(exento)' : ''}:</span><span className="font-medium">{money(totals.total_iva)}</span></div>
              {totals.total_retencion > 0 && <div className="flex justify-between text-slate-500"><span>Retención ({form.retencion_porcentaje}%):</span><span>- {money(totals.total_retencion)}</span></div>}
              <div className="border-t pt-2 flex justify-between text-lg"><span className="font-bold">TOTAL:</span><span className="font-bold text-[#004E89]">{money(totals.total)}</span></div>
              {doc?.total_pagado > 0 && (
                <div className="flex justify-between text-green-700"><span>Pagado:</span><span>{money(doc.total_pagado)}</span></div>
              )}
              {doc?.total_pagado > 0 && (
                <div className="flex justify-between text-red-700"><span>Pendiente:</span><span>{money((doc.total || 0) - (doc.total_pagado || 0))}</span></div>
              )}
            </div>
            {doc?.pagos && doc.pagos.length > 0 && (
              <div className="mt-4">
                <h4 className="text-xs font-semibold text-slate-500 uppercase mb-2">Pagos registrados</h4>
                <div className="space-y-1 text-xs">
                  {doc.pagos.map(p => (
                    <div key={p.id} className="flex justify-between border-b pb-1">
                      <span>{p.fecha} · {p.metodo}</span>
                      <span className="font-medium">{money(p.importe)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Dialog: Email */}
        <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
          <DialogContent>
            <DialogHeader><DialogTitle>Enviar por email</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Para *</Label><Input type="email" value={emailForm.to} onChange={e=>setEmailForm(p=>({...p, to: e.target.value}))} required /></div>
              <div><Label>Asunto (opcional)</Label><Input value={emailForm.subject} onChange={e=>setEmailForm(p=>({...p, subject: e.target.value}))} placeholder="Se generará automáticamente si vacío" /></div>
              <div><Label>Mensaje (opcional, HTML)</Label><textarea value={emailForm.body} onChange={e=>setEmailForm(p=>({...p, body: e.target.value}))} className="w-full mt-1 rounded-md border border-slate-300 px-3 py-2 text-sm" rows={6} /></div>
              <p className="text-xs text-slate-500">El PDF se adjunta automáticamente. Se envía copia (CC) a la empresa.</p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={()=>setShowEmailDialog(false)}>Cancelar</Button>
                <Button onClick={sendEmail} className="bg-[#004E89] hover:bg-[#003a68]"><Mail className="w-4 h-4 mr-1" /> Enviar</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Dialog: Payment */}
        <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
          <DialogContent>
            <DialogHeader><DialogTitle>Registrar pago</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Fecha</Label><Input type="date" value={payForm.fecha} onChange={e=>setPayForm(p=>({...p, fecha: e.target.value}))} /></div>
              <div><Label>Importe *</Label><Input type="number" step="0.01" value={payForm.importe} onChange={e=>setPayForm(p=>({...p, importe: e.target.value}))} required /></div>
              <div><Label>Método</Label>
                <select value={payForm.metodo} onChange={e=>setPayForm(p=>({...p, metodo: e.target.value}))} className="w-full mt-1 rounded-md border border-slate-300 px-3 py-2 text-sm">
                  <option>Transferencia</option><option>Domiciliación</option><option>Efectivo</option><option>Tarjeta</option><option>Otro</option>
                </select>
              </div>
              <div><Label>Notas</Label><Input value={payForm.notas} onChange={e=>setPayForm(p=>({...p, notas: e.target.value}))} /></div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={()=>setShowPaymentDialog(false)}>Cancelar</Button>
                <Button onClick={addPayment} className="bg-green-600 hover:bg-green-700">Registrar</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default SalesDocEditor;
